import '../../../ui-styles/ibest-style.css';
import { useMemo } from 'react';
import { defineFields, mergeInitialValues, useAnalysisWorkflow, useAppStorage, useCalcId, useRHFWithZod, useSavedCalculationLoader, useUrlFormValues } from '../shared';
import { analyze } from './api';
import type { APIResponse } from './appConfig';
import { APP_NAME, APP_VERSION, APP_VERSION_POLICY, CONFINEMENT_OPTIONS, RESPONSE_DISPLAY_CONFIG } from './appConfig';
import { schema } from './validation';
import type { FormValues, ParsedValues } from './validation';
import { ProjectInput, PrintPortal, FormFields, ResponseDisplay, GenerateLinkButton } from '../shared';
import { generateShareableURL } from '../shared/urlUtils';

export default function App() {
  const calcId = useCalcId();
  const urlFormValues = useUrlFormValues(calcId);

  const initialValues: FormValues = useMemo(
    () =>
      mergeInitialValues(
        {
          project: 'Default',
          title: 'VCE Blast',
          mass: 100,
          LHV: 50,
          mf: 0.59,
          R: 50,
          Confinement: 'UnConfined',
          V: '60*60*15',
          A: '10*10'
        },
        urlFormValues
      ),
    [urlFormValues]
  );

  const {
    register,
    formState: { errors },
    watch,
    reset
  } = useRHFWithZod<FormValues>({ schema, defaultValues: initialValues });

  const values = watch();
  const { saveDataToDB } = useAppStorage<FormValues, APIResponse>(APP_NAME, APP_VERSION);
  const { analysisResult, setAnalysisResult, isAnalyzing, saveStatus, handleAnalyze, handleSave } = useAnalysisWorkflow<FormValues, ParsedValues, APIResponse>({
    errors,
    values,
    schema,
    analyze,
    saveDataToDB
  });

  useSavedCalculationLoader<FormValues, APIResponse>(calcId, reset, setAnalysisResult, { appName: APP_NAME, versionPolicy: APP_VERSION_POLICY });

  const fields = defineFields<FormValues>([
    { kind: 'input', name: 'title', label: 'Title', helpText: 'Calculation title', placeholder: 'VCE Blast Case' },
    { kind: 'input', name: 'mass', label: 'Vaporized Mass', unit: 'kg', helpText: 'Fuel mass involved in blast', placeholder: '100' },
    { kind: 'input', name: 'LHV', label: 'Low Heat Value', unit: 'MJ/kg', helpText: 'Fuel lower heating value', placeholder: '45' },
    { kind: 'input', name: 'mf', label: 'Mach Factor', unit: '', helpText: 'Blast Mach factor', placeholder: '1.0' },
    { kind: 'input', name: 'R', label: 'Standoff Distance', unit: 'ft', helpText: 'Distance from blast center', placeholder: '50' },
    { kind: 'dropdown', name: 'Confinement', label: 'Confinement', options: CONFINEMENT_OPTIONS, helpText: 'Select Confined or UnConfined' },
    {
      kind: 'input',
      name: 'V',
      label: 'Vessel Volume',
      unit: 'ft^3',
      helpText: 'Total vessel volume',
      placeholder: '1000',
      show: values.Confinement === 'Confined'
    },
    {
      kind: 'input',
      name: 'A',
      label: 'Vessel Surface Area',
      unit: 'ft^2',
      helpText: 'Total vessel surface area',
      placeholder: '600',
      show: values.Confinement === 'Confined'
    }
  ]);

  return (
    <>
      <div className="analysis-app analysis-app--teal analysis-app__layout layout-3col print:hidden w-full">
        <div className="analysis-app__hero">
          <div>
            <h2 className="analysis-app__title">{APP_NAME}</h2>
            <p className="analysis-app__subtitle">Engineering Analysis Module</p>
          </div>
        </div>
        <div className="analysis-app__controls flex flex-col rounded-xl p-1 m-1 gap-y-0 shadow-div">
          <ProjectInput error={errors.project?.message ?? null} {...register('project')} />

          <FormFields<FormValues> fields={fields} register={register} errors={errors} />

          <div className="analysis-app__actions flex flex-row w-full mt-1 gap-2">
            <button className="submit-button" onClick={handleAnalyze} disabled={Object.keys(errors).length > 0 || isAnalyzing}>
              {isAnalyzing ? 'Analyzing...' : 'Analyze'}
            </button>
            <button className="submit-button" onClick={handleSave} disabled={Object.keys(errors).length > 0 || saveStatus === 'saving'}>
              {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'success' ? 'Saved!' : saveStatus === 'error' ? 'Error!' : 'Save'}
            </button>
            <GenerateLinkButton formData={values} />
          </div>
        </div>

        <div className="analysis-app__results-wrap grid grid-rows-[auto_1fr] rounded-xl gap-y-0 w-fit">
          <div className="analysis-app__results shadow-div rounded-xl m-1">
            <ResponseDisplay data={analysisResult} sections={RESPONSE_DISPLAY_CONFIG} emptyMessage="No analysis results yet." />
          </div>

          <div className="analysis-app__charts flex flex-col shadow-div my-2 m-1 rounded-xl">
            {analysisResult?.charts && (
              <div className="analysis-app__charts-grid grid grid-cols-2 gap-2 m-5 print:gap-1">
                {Object.entries(analysisResult.charts)
                  .filter(([, v]) => !!v)
                  .map(([key, v]) => {
                    const chart = v!;
                    const src = `data:${chart.mime};base64,${chart.base64}`;

                    return (
                      <div key={key} className="analysis-app__chart-card break-inside-avoid">
                        <img src={src} alt={`${key} chart`} loading="lazy" className="w-full h-auto object-contain rounded-none max-w-250" />
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>

        <div className="print:hidden hidden lg:block"></div>
      </div>

      <PrintPortal>
        <div className="hidden print:block print-root ">
          <div className="print-a4-landscape border border-black box-border bg-white">
            <div className="hidden print:grid h-full min-h-0 grid-cols-[24%_76%] print:bg-white print:text-black">
              <div className="flex h-full min-h-0 flex-col">
                <div className="p-1">
                  <div className="text-[10px] font-semibold m-0 p-0 leading-none">{values.title || 'iSteel Element'}</div>
                  <div className="text-[10px] opacity-70 m-0 p-0 leading-none mb-1">Project: {values.project || '—'}</div>

                  <div className="m-0 p-0 ">
                    <table className="w-full table-fixed text-[10px] m-0 p-0">
                      <colgroup>
                        <col className="w-[55%]" />
                        <col className="w-[45%]" />
                      </colgroup>

                      <caption className="caption-top text-[10px] font-semibold m-0 p-0 leading-none">Inputs</caption>
                      <tbody className="[&>tr>td]:p-1 [&>tr>td]:leading-none">
                        {[
                          ['Mass', `${values.mass} kg`],
                          ['LHV', `${values.LHV} MJ/kg`],
                          ['Mach Factor', values.mf],
                          ['Standoff', `${values.R} ft`],
                          ['Confinement', values.Confinement],
                          ['Volume', `${values.V} ft^3`],
                          ['Surface Area', `${values.A} ft^2`]
                        ].map(([label, value], i) => (
                          <tr key={i}>
                            <td className="font-semibold">{label}</td>
                            <td>{value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="overflow-hidden border-t">
                  <div className="p-1">
                    {analysisResult && (
                      <table className="w-full table-fixed text-[10px] m-0 p-0">
                        <colgroup>
                          <col className="w-[55%]" />
                          <col className="w-[45%]" />
                        </colgroup>

                        <caption className="caption-top text-[10px] font-semibold m-0 p-0 leading-none">Results</caption>

                        <tbody className="[&>tr>td]:p-1 [&>tr>td]:leading-none">
                          {[
                            ['E', <span>{analysisResult.E} mJ</span>],
                            [
                              <span>
                                P<sub>0</sub>
                              </span>,
                              <span>{analysisResult.Patm} mPa</span>
                            ],
                            [
                              <span>
                                a<sub>0</sub>
                              </span>,
                              <span>{analysisResult.Patm} mPa</span>
                            ],
                            [
                              <span>
                                R<sub>bar</sub>
                              </span>,
                              <span>{analysisResult.Rbar}</span>
                            ],
                            [
                              <span>
                                P<sub>bar</sub>
                              </span>,
                              <span>{analysisResult.Pbar}</span>
                            ],
                            [
                              <span>
                                I<sub>bar</sub>
                              </span>,
                              <span>{analysisResult.Ibar}</span>
                            ],
                            ['R', <span>{analysisResult.R} ft</span>],
                            [
                              <span>
                                P<sub>i</sub>
                              </span>,
                              <span>{analysisResult.Pi} psi</span>
                            ],
                            [
                              <span>
                                I<sub>i</sub>
                              </span>,
                              <span>{analysisResult.Ii} psi-msec</span>
                            ],
                            [
                              <span>
                                P<sub>r</sub>
                              </span>,
                              <span>{analysisResult.Pr} psi</span>
                            ],
                            [
                              <span>
                                I<sub>r</sub>
                              </span>,
                              <span>{analysisResult.Ir} psi-msec</span>
                            ],
                            [
                              <span>
                                W<sub>g</sub>
                              </span>,
                              <span>{analysisResult.Wg} lb</span>
                            ],
                            [
                              <span>
                                P<sub>g</sub>
                              </span>,
                              <span>{analysisResult.Pi} psi</span>
                            ],
                            [
                              <span>
                                I<sub>g</sub>
                              </span>,
                              <span>{analysisResult.Ig} psi-msec</span>
                            ]
                          ].map(([label, value], i) => (
                            <tr key={i}>
                              <td className="font-semibold">{label}</td>
                              <td>{value}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
                <div className="mt-auto p-1 text-[9px]">
                  <a href={generateShareableURL(values, window.location.pathname)} className="text-blue-600 hover:underline">
                    🔗 LINK
                  </a>
                </div>
              </div>

              <div className="h-full min-h-0 overflow-hidden border-l">
                <div className="h-full min-h-0 pl-2 overflow-hidden">
                  {analysisResult?.charts && (
                    <div className="grid h-full grid-cols-2 grid-rows-[60%_40%] gap-0 overflow-hidden border-0 p-0 box-border">
                      {Object.entries(analysisResult.charts)
                        .filter(([, v]) => !!v)
                        .map(([key, v]) => {
                          const chart = v!;
                          const src = `data:${chart.mime};base64,${chart.base64}`;

                          return (
                            <div key={key} className="h-full min-h-0 min-w-0 overflow-hidden break-inside-avoid">
                              <img src={src} alt={`${key} chart`} loading="lazy" className="block h-full w-full max-h-full max-w-full object-contain rounded-none" />
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </PrintPortal>
    </>
  );
}
